import React, { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Drawer, Input, message, notification,
  Space, Spin, Table, Tooltip, Tag, Tabs,
} from "antd";
import type { InputRef, TableColumnsType, TablePaginationConfig } from "antd";
import {
  AppstoreOutlined, DownloadOutlined, FilterOutlined,
  FundOutlined, SearchOutlined, TeamOutlined, TrophyOutlined,
  DollarOutlined, ReloadOutlined, ApartmentOutlined,
  FileTextOutlined, ToolOutlined, HomeOutlined,
  ArrowRightOutlined, EyeOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchBuAndClientDetails_OBO } from "../../store/reducers/orderDashboardSlice";
import {
  fetchOBOReport_AllOpportunities, fetchOBOReport_AllChildOpps,
  fetchOBOReport_AllSOWs, fetchOBOReport_AllWOs,
  fetchOBOReport_ChartData, getOboDownloadReport, setOBOFilterData,
} from "../../store/reducers/oboDashboardSlice";
import {
  formatNumberWithCommas, formatToTwoDecimalPlaces, rejectedApiMessage,
} from "../../utilities/CommonHelper";
import { ColumnType, FilterConfirmProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import OboFilterDrawer, { FilterValues } from "./OboFilterDrawer";
import "../../styles/oboDashboardV2.css";

/* ─── helpers ─── */
const fmtShort = (n: number): string => {
  if (!n && n !== 0) return "$0";
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000)     return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)         return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtFull = (n: any): string =>
  n || n === 0 ? `$${formatNumberWithCommas(formatToTwoDecimalPlaces(n))}` : "—";
const addKeys = (data: any[]) =>
  data.map((item, i) => ({
    ...item,
    _key: `${item.opportunityID || item.sowid || item.woId || i}_${i}`,
  }));

/* ---------- themed cell renderers ---------- */
const CellID = ({ val, color = "#1e40af", bg = "#eff6ff" }: { val: string; color?: string; bg?: string }) => (
  <span style={{
    fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 11.5, fontWeight: 600,
    color, background: bg, padding: "2px 7px", borderRadius: 5, letterSpacing: "0.2px",
    whiteSpace: "nowrap",
  }}>{val || "—"}</span>
);

const CellMoney = ({ val, color, bg }: { val: any; color: string; bg: string }) => (
  <span style={{
    fontWeight: 700, color, background: bg,
    padding: "2px 8px", borderRadius: 6, fontSize: 12, whiteSpace: "nowrap",
  }}>{fmtFull(val)}</span>
);

const CellLevel = ({ val }: { val: string }) => {
  const isParent = (val || "").toLowerCase().includes("parent");
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
      background: isParent ? "#dbeafe" : "#ede9fe",
      color: isParent ? "#1d4ed8" : "#6d28d9",
      letterSpacing: "0.2px", whiteSpace: "nowrap",
    }}>{val || "—"}</span>
  );
};

const CellCurrency = ({ val }: { val: string }) => {
  const colors: Record<string, { bg: string; color: string }> = {
    USD: { bg: "#dcfce7", color: "#15803d" },
    EUR: { bg: "#dbeafe", color: "#1d4ed8" },
    GBP: { bg: "#fef9c3", color: "#854d0e" },
    INR: { bg: "#fce7f3", color: "#9d174d" },
  };
  const c = colors[val] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
      background: c.bg, color: c.color, letterSpacing: "0.3px",
    }}>{val || "—"}</span>
  );
};

const CellClient = ({ val }: { val: string }) => (
  <Tooltip title={val}>
    <span style={{
      fontWeight: 600, color: "#0f172a", fontSize: 12,
      display: "block", maxWidth: 145,
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>{val || "—"}</span>
  </Tooltip>
);

const CellBU = ({ val }: { val: string }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 8,
    background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa",
    whiteSpace: "nowrap",
  }}>{val || "—"}</span>
);

const CellCount = ({ val, color, bg, label }: { val: any; color: string; bg: string; label: string }) =>
  (val || 0) > 0 ? (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      fontWeight: 700, fontSize: 12, color,
      background: bg, padding: "2px 9px", borderRadius: 10,
    }}>{val} <span style={{ fontSize: 10, opacity: 0.8 }}>{label}</span></span>
  ) : <span style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>;

/* ─── level badge ─── */
const LevelBadge = ({ type }: { type: "parent" | "child" | "sow" | "wo" }) => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    parent: { label: "OPP",   color: "#034EA2", bg: "#dbeafe" },
    child:  { label: "CHILD", color: "#7c3aed", bg: "#ede9fe" },
    sow:    { label: "SOW",   color: "#065f46", bg: "#d1fae5" },
    wo:     { label: "WO",    color: "#92400e", bg: "#fef3c7" },
  };
  const s = map[type];
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
      background: s.bg, color: s.color, letterSpacing: "0.4px",
    }}>{s.label}</span>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   DRILL-DOWN DRAWER
═══════════════════════════════════════════════════════════════════ */
type DrillLevel = "child" | "sow" | "wo";

interface DrillState {
  level: DrillLevel;
  parentOpp: any;          // always set
  selectedChild?: any;     // set when at sow/wo level (from child nav)
  selectedSOW?: any;       // set when at wo level
  hasChildren?: boolean;   // whether parent has child opps
}

const DrillDownDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  rootOpp: any | null;
}> = ({ open, onClose, rootOpp }) => {
  const dispatch = useDispatch();
  const oboFilterData = useSelector((state: any) => state.oboReports.filterData);

  const [drill, setDrill] = useState<DrillState | null>(null);

  /* rows / loading / pagination for each level */
  const [childRows, setChildRows]   = useState<any[]>([]);
  const [childTotal, setChildTotal] = useState(0);
  const [childPage, setChildPage]   = useState(0);
  const [childLoading, setChildLoading] = useState(false);

  const [sowRows, setSowRows]     = useState<any[]>([]);
  const [sowTotal, setSowTotal]   = useState(0);
  const [sowPage, setSowPage]     = useState(0);
  const [sowLoading, setSowLoading] = useState(false);

  const [woRows, setWoRows]     = useState<any[]>([]);
  const [woTotal, setWoTotal]   = useState(0);
  const [woPage, setWoPage]     = useState(0);
  const [woLoading, setWoLoading] = useState(false);

  /* ── init when drawer opens ── */
  useEffect(() => {
    if (open && rootOpp) {
      setChildRows([]); setChildTotal(0); setChildPage(0);
      setSowRows([]);   setSowTotal(0);   setSowPage(0);
      setWoRows([]);    setWoTotal(0);    setWoPage(0);
      const hasChildren = (rootOpp.countOfChildOpp || 0) > 0;
      if (hasChildren) {
        setDrill({ level: "child", parentOpp: rootOpp, hasChildren: true });
        loadChildren(rootOpp, 0);
      } else {
        // No child opps → go directly to SOWs for this parent opp
        setDrill({ level: "sow", parentOpp: rootOpp, hasChildren: false });
        loadSOWs({ ...rootOpp, level: "parent opportunity" }, 0);
      }
    }
  }, [open, rootOpp?.opportunityID]); // eslint-disable-line

  /* ── loaders ── */
  const getSelectedBu = (buArr: any[]) => (buArr || []).map((b: any) => b.value || b);

  const loadChildren = useCallback((opp: any, page: number) => {
    if (!opp?.opportunityID) return;
    setChildLoading(true);
    dispatch(fetchOBOReport_AllChildOpps({
      payload: {
        parentOppID: opp.opportunityID,
        startDate: oboFilterData.startDate,
        endDate: oboFilterData.endDate,
        clientName: oboFilterData.clientName,
        buName: getSelectedBu(oboFilterData.buName),
        page, size: 10, filters: [],
      },
    })).then((res: any) => {
      setChildLoading(false);
      if (res.type === "oboReports/fetchOBOReport_AllChildOpps/fulfilled") {
        setChildRows(addKeys(res.payload?.content || []));
        setChildTotal(res.payload?.totalElements || 0);
        setChildPage(page);
      }
    });
  }, [oboFilterData]); // eslint-disable-line

  const loadSOWs = useCallback((opp: any, page: number) => {
    if (!opp?.opportunityID) return;
    setSowLoading(true);
    dispatch(fetchOBOReport_AllSOWs({
      payload: {
        opportunityID: opp.opportunityID,
        startDate: oboFilterData.startDate,
        endDate: oboFilterData.endDate,
        isParent: opp.level === "parent opportunity",
        page, size: 10, filters: [],
      },
    })).then((res: any) => {
      setSowLoading(false);
      if (res.type === "oboReports/fetchOBOReport_AllSOWs/fulfilled") {
        setSowRows(res.payload?.content || []);
        setSowTotal(res.payload?.totalElements || 0);
        setSowPage(page);
      } else {
        message.warning("No SOW data found.");
      }
    });
  }, [oboFilterData]); // eslint-disable-line

  const loadWOs = useCallback((sow: any, page: number) => {
    if (!sow?.sowid) return;
    setWoLoading(true);
    dispatch(fetchOBOReport_AllWOs({
      payload: {
        sowId: sow.sowid,
        startDate: oboFilterData.startDate,
        endDate: oboFilterData.endDate,
        page, size: 10, filters: [],
      },
    })).then((res: any) => {
      setWoLoading(false);
      if (res.type === "oboReports/fetchOBOReport_AllWOs/fulfilled") {
        setWoRows(res.payload?.content || []);
        setWoTotal(res.payload?.totalElements || 0);
        setWoPage(page);
      }
    });
  }, [oboFilterData]); // eslint-disable-line

  /* ── navigation ── */
  const goToSOWs = (child: any) => {
    setSowRows([]); setSowTotal(0); setSowPage(0);
    setDrill((d) => d ? { ...d, level: "sow", selectedChild: child } : d);
    // child opps: isParent = false
    loadSOWs({ ...child, level: "child opportunity" }, 0);
  };

  const goToSOWsFromParent = () => {
    // called when going back to direct-SOW view (no children case)
    if (!drill) return;
    setSowRows([]); setSowTotal(0); setSowPage(0);
    setDrill((d) => d ? { level: "sow", parentOpp: d.parentOpp, hasChildren: false } : d);
    loadSOWs({ ...drill.parentOpp, level: "parent opportunity" }, 0);
  };

  const goToWOs = (sow: any) => {
    setWoRows([]); setWoTotal(0); setWoPage(0);
    setDrill((d) => d ? { ...d, level: "wo", selectedSOW: sow } : d);
    loadWOs(sow, 0);
  };

  const goBack = (targetLevel: DrillLevel) => {
    if (!drill) return;
    if (targetLevel === "child") {
      setDrill({ level: "child", parentOpp: drill.parentOpp, hasChildren: true });
    } else if (targetLevel === "sow") {
      setSowRows([]); setSowTotal(0); setSowPage(0);
      setDrill({ level: "sow", parentOpp: drill.parentOpp, selectedChild: drill.selectedChild, hasChildren: drill.hasChildren });
      if (!drill.hasChildren) {
        // No children path: reload SOWs directly for the parent opp
        loadSOWs({ ...drill.parentOpp, level: "parent opportunity" }, 0);
      } else if (drill.selectedChild) {
        // Has children path: reload SOWs for the previously selected child
        loadSOWs({ ...drill.selectedChild, level: "child opportunity" }, 0);
      }
    }
  };

  /* ── breadcrumb ── */
  const BreadcrumbBar = () => {
    if (!drill) return null;

    type CrumbItem = { label: React.ReactNode; active: boolean; onClick?: () => void };
    const crumbs: CrumbItem[] = [];

    // Root opp crumb — always present
    // Active (non-clickable) only when at child level, or at sow level with no children
    const isRootActive = drill.level === "child" || (drill.level === "sow" && !drill.hasChildren);
    // Root is clickable when: has children and not at child level, OR no children and at WO level
    const rootClickTarget: DrillLevel | null =
      !drill.hasChildren && drill.level === "wo" ? "sow"
      : drill.hasChildren && drill.level !== "child" ? "child"
      : null;
    crumbs.push({
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <HomeOutlined style={{ fontSize: 11 }} />
          <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
            {drill.parentOpp?.opportunityID}
          </span>
        </span>
      ),
      active: isRootActive,
      onClick: rootClickTarget ? () => goBack(rootClickTarget) : undefined,
    });

    // Child opp crumb — only if this opp had children and we're at sow or wo level
    if (drill.hasChildren && (drill.level === "sow" || drill.level === "wo")) {
      crumbs.push({
        label: (
          <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
            {drill.selectedChild?.opportunityID || "Child Opp"}
          </span>
        ),
        active: drill.level === "sow",
        onClick: drill.level === "wo" ? () => goBack("sow") : undefined,
      });
    }

    // SOW crumb — when at WO level (both with and without child opps)
    if (drill.level === "wo") {
      crumbs.push({
        label: <span>{drill.selectedSOW?.sowid || "SOW"}</span>,
        active: true,
        // SOW crumb is the current level label — clicking back to SOW is done via the ← button on the crumb above
      });
    }

    const levelColors: Record<DrillLevel, { bg: string; color: string; label: string }> = {
      child: { bg: "#ede9fe", color: "#7c3aed", label: "CHILD OPPS" },
      sow:   { bg: "#d1fae5", color: "#065f46", label: "SOWs" },
      wo:    { bg: "#fef3c7", color: "#92400e", label: "WORK ORDERS" },
    };
    const lc = levelColors[drill.level];

    return (
      <div style={{
        background: "#f1f5f9", borderBottom: "1px solid #e2e8f0",
        padding: "10px 20px", display: "flex", alignItems: "center", gap: 8,
      }}>
        <Breadcrumb
          items={crumbs.map((c) => ({
            title: c.onClick ? (
              <span
                onClick={c.onClick}
                style={{ cursor: "pointer", color: "#034EA2", fontWeight: 600, fontSize: 12,
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "2px 8px", borderRadius: 6, background: "#e0e7ff",
                  transition: "background 0.2s",
                }}
              >
                ← {c.label}
              </span>
            ) : (
              <span style={{ color: "#334155", fontWeight: c.active ? 700 : 500, fontSize: 12 }}>{c.label}</span>
            ),
          }))}
          separator={<ArrowRightOutlined style={{ fontSize: 9, color: "#94a3b8" }} />}
        />
        <span style={{
          marginLeft: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px",
          padding: "3px 10px", borderRadius: 12, background: lc.bg, color: lc.color,
        }}>
          {lc.label}
        </span>
      </div>
    );
  };

  /* ── columns ── */
  const childCols: TableColumnsType<any> = [
    { title: "Opportunity ID", dataIndex: "opportunityID", width: 150, fixed: "left", render: (v: string) => <CellID val={v} /> },
    { title: "Opportunity Name", dataIndex: "opportunityName", width: 190,
      render: (t: string) => <Tooltip title={t}><span style={{ display: "block", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500, color: "#1e293b" }}>{t}</span></Tooltip> },
    { title: "Client",  dataIndex: "client",   width: 120, render: (v: string) => <CellClient val={v} /> },
    { title: "BU",      dataIndex: "bu",       width: 100, render: (v: string) => <CellBU val={v} /> },
    { title: "TCV",     dataIndex: "sumOfTCV", width: 125, render: (v: any) => <CellMoney val={v} color="#0e7490" bg="#ecfeff" /> },
    { title: "Revenue", dataIndex: "revenue",  width: 125, render: (v: any) => <CellMoney val={v} color="#b45309" bg="#fffbeb" /> },
    { title: "OBO",     dataIndex: "obo",      width: 115, render: (v: any) => <CellMoney val={v} color="#7c3aed" bg="#f5f3ff" /> },
    {
      title: "SOWs", dataIndex: "countOfSowDetails", width: 100, fixed: "right",
      render: (v: any, rec: any) =>
        v > 0 ? (
          <Button
            size="small" type="primary"
            style={{ background: "#059669", borderColor: "#059669", fontSize: 12, fontWeight: 600 }}
            onClick={() => goToSOWs(rec)}
          >
            {v} SOW →
          </Button>
        ) : <span style={{ color: "#cbd5e1", fontSize: 12 }}>No SOWs</span>,
    },
  ];

  const sowCols: TableColumnsType<any> = [
    { title: "SOW ID", dataIndex: "sowid", width: 155, fixed: "left", render: (v: string) => <CellID val={v} color="#065f46" bg="#d1fae5" /> },
    { title: "Start Date", dataIndex: "sowStartDate", width: 115, render: (v: string) => <span style={{ color: "#1d4ed8", fontWeight: 600, fontSize: 12 }}>{v || "—"}</span> },
    { title: "End Date",   dataIndex: "sowEndDate",   width: 115, render: (v: string) => <span style={{ color: "#9f1239", fontWeight: 600, fontSize: 12 }}>{v || "—"}</span> },
    { title: "Currency",   dataIndex: "currency",     width: 85,  render: (v: string) => <CellCurrency val={v} /> },
    { title: "Level",      dataIndex: "level",        width: 140, render: (v: string) => <CellLevel val={v} /> },
    { title: "TCV",       dataIndex: "sumOfTCV",     width: 125, render: (v: any) => <CellMoney val={v} color="#0e7490" bg="#ecfeff" /> },
    { title: "Revenue",   dataIndex: "revenue",      width: 125, render: (v: any) => <CellMoney val={v} color="#b45309" bg="#fffbeb" /> },
    { title: "Total Rev", dataIndex: "totalRevenue", width: 115, render: (v: any) => <CellMoney val={v} color="#15803d" bg="#f0fdf4" /> },
    { title: "OBO",       dataIndex: "obo",          width: 115, render: (v: any) => <CellMoney val={v} color="#7c3aed" bg="#f5f3ff" /> },
    {
      title: "Work Orders", dataIndex: "countOfWorkOrder", width: 110, fixed: "right",
      render: (v: any, rec: any) =>
        v > 0 ? (
          <Button
            size="small" type="primary"
            style={{ background: "#d97706", borderColor: "#d97706", fontSize: 12, fontWeight: 600 }}
            onClick={() => goToWOs(rec)}
          >
            {v} WO →
          </Button>
        ) : <span style={{ color: "#cbd5e1", fontSize: 12 }}>No WOs</span>,
    },
  ];

  const woCols: TableColumnsType<any> = [
    { title: "WO ID", dataIndex: "woId", width: 155, fixed: "left", render: (v: string) => <CellID val={v} color="#92400e" bg="#fef3c7" /> },
    { title: "Parent ID", dataIndex: "parentId",     width: 155, render: (v: string) => <CellID val={v} color="#374151" bg="#f3f4f6" /> },
    { title: "Currency",  dataIndex: "currency",     width: 85,  render: (v: string) => <CellCurrency val={v} /> },
    { title: "Level",     dataIndex: "level",        width: 140, render: (v: string) => <CellLevel val={v} /> },
    { title: "TCV",       dataIndex: "sumOfTCV",     width: 125, render: (v: any) => <CellMoney val={v} color="#0e7490" bg="#ecfeff" /> },
    { title: "Revenue",   dataIndex: "revenue",      width: 125, render: (v: any) => <CellMoney val={v} color="#b45309" bg="#fffbeb" /> },
    { title: "Total Rev", dataIndex: "totalRevenue", width: 115, render: (v: any) => <CellMoney val={v} color="#15803d" bg="#f0fdf4" /> },
    { title: "OBO Value", dataIndex: "obo",          width: 115, render: (v: any) => <CellMoney val={v} color="#7c3aed" bg="#f5f3ff" /> },
  ];

  /* ── level header info ── */
  const sowLevelLabel = drill?.hasChildren ? "SOWs (via Child Opp)" : "SOWs (Direct)";
  const levelMeta = {
    child: { icon: <ApartmentOutlined />, color: "#7c3aed", bg: "#f5f3ff", label: "Child Opportunities", total: childTotal },
    sow:   { icon: <FileTextOutlined />,  color: "#065f46", bg: "#f0fdf4", label: sowLevelLabel,         total: sowTotal   },
    wo:    { icon: <ToolOutlined />,      color: "#92400e", bg: "#fffbeb", label: "Work Orders",          total: woTotal    },
  };

  const currentMeta = drill ? levelMeta[drill.level] : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={900}
      styles={{
        header: { background: "#034EA2", padding: "16px 20px" },
        body: { padding: 0, background: "#f8fafc" },
      }}
      title={
        <div style={{ color: "#fff" }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {rootOpp?.opportunityName || rootOpp?.opportunityID || "Opportunity Details"}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2, display: "flex", gap: 12 }}>
            <span>{rootOpp?.client}</span>
            {rootOpp?.bu && <span>· {rootOpp?.bu}</span>}
            {rootOpp?.currency && <span>· {rootOpp?.currency}</span>}
          </div>
        </div>
      }
      closable
    >
      {/* Breadcrumb nav */}
      <BreadcrumbBar />

      {/* Level header */}
      {drill && currentMeta && (
        <div style={{
          background: currentMeta.bg, borderBottom: `2px solid`,
          borderColor: drill.level === "child" ? "#c4b5fd" : drill.level === "sow" ? "#6ee7b7" : "#fcd34d",
          padding: "12px 20px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 18, color: currentMeta.color }}>{currentMeta.icon}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: currentMeta.color }}>{currentMeta.label}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              <span style={{ fontWeight: 600 }}>{currentMeta.total}</span> record{currentMeta.total !== 1 ? "s" : ""}
              {drill.level === "child" && (
                <span style={{ marginLeft: 8, color: "#7c3aed", background: "#ede9fe", padding: "1px 7px", borderRadius: 8, fontSize: 11 }}>
                  Click <strong>N SOW →</strong> button to see SOWs
                </span>
              )}
              {drill.level === "sow" && (
                <span style={{ marginLeft: 8, color: "#065f46", background: "#d1fae5", padding: "1px 7px", borderRadius: 8, fontSize: 11 }}>
                  Click <strong>N WO →</strong> button to see Work Orders
                </span>
              )}
            </div>
          </div>
          {/* Summary KPIs for current view */}
          {drill.level === "child" && childRows.length > 0 && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
              {[
                { label: "TCV", val: childRows.reduce((s, r) => s + (r.sumOfTCV || 0), 0) },
                { label: "OBO", val: childRows.reduce((s, r) => s + (r.obo || 0), 0) },
              ].map((k) => (
                <div key={k.label} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{k.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: currentMeta.color }}>{fmtShort(k.val)}</div>
                </div>
              ))}
            </div>
          )}
          {drill.level === "sow" && sowRows.length > 0 && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
              {[
                { label: "TCV", val: sowRows.reduce((s, r) => s + (r.sumOfTCV || 0), 0) },
                { label: "OBO", val: sowRows.reduce((s, r) => s + (r.obo || 0), 0) },
              ].map((k) => (
                <div key={k.label} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{k.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: currentMeta.color }}>{fmtShort(k.val)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table area */}
      <div style={{ padding: "16px 20px" }}>
        {drill?.level === "child" && (
          <Spin spinning={childLoading}>
            <Table
              size="small"
              columns={childCols}
              dataSource={childRows}
              rowKey={(r) => r.opportunityID || r._key}
              scroll={{ x: "max-content", y: 460 }}
              pagination={{
                current: childPage + 1, pageSize: 10, total: childTotal,
                showSizeChanger: false, showTotal: (t) => `${t} child opps`,
                onChange: (p) => { setChildPage(p - 1); loadChildren(drill.parentOpp, p - 1); },
              }}
              locale={{ emptyText: <div style={{ padding: 32, color: "#94a3b8" }}>No child opportunities found.</div> }}
            />
          </Spin>
        )}

        {drill?.level === "sow" && (
          <Spin spinning={sowLoading}>
            <Table
              size="small"
              columns={sowCols}
              dataSource={sowRows}
              rowKey={(r) => r.sowid || r._key}
              scroll={{ x: "max-content", y: 460 }}
              pagination={{
                current: sowPage + 1, pageSize: 10, total: sowTotal,
                showSizeChanger: false, showTotal: (t) => `${t} SOWs`,
                onChange: (p) => {
                  setSowPage(p - 1);
                  if (drill.selectedChild) loadSOWs({ ...drill.selectedChild, level: "child opportunity" }, p - 1);
                },
              }}
              locale={{ emptyText: <div style={{ padding: 32, color: "#94a3b8" }}>No SOWs found.</div> }}
            />
          </Spin>
        )}

        {drill?.level === "wo" && (
          <Spin spinning={woLoading}>
            <Table
              size="small"
              columns={woCols}
              dataSource={woRows}
              rowKey={(r) => r.woId || r._key}
              scroll={{ x: "max-content", y: 460 }}
              pagination={{
                current: woPage + 1, pageSize: 10, total: woTotal,
                showSizeChanger: false, showTotal: (t) => `${t} work orders`,
                onChange: (p) => { setWoPage(p - 1); if (drill.selectedSOW) loadWOs(drill.selectedSOW, p - 1); },
              }}
              locale={{ emptyText: <div style={{ padding: 32, color: "#94a3b8" }}>No work orders found.</div> }}
            />
          </Spin>
        )}
      </div>
    </Drawer>
  );
};

/* ─── default date range: first day of 6 months ago → last day of current month ─── */
const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0); // day 0 = last day of current month
  const pad   = (n: number) => String(n).padStart(2, "0");
  return {
    startDate: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`,
    endDate:   `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
  };
};
const DEFAULT_DATES = getDefaultDateRange();

/* ═══════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════════════════════════ */
const OboDashboardV2: React.FC = () => {
  const dispatch = useDispatch();
  const oboReportData    = useSelector((state: any) => state.oboReports);
  const orderDashboardData = useSelector((state: any) => state.orderDashboard);

  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState<FilterValues>({
    clientName: [], buName: [], startDate: DEFAULT_DATES.startDate, endDate: DEFAULT_DATES.endDate,
  });
  const [defaultFiltersForDrawer, setDefaultFiltersForDrawer] = React.useState<FilterValues>({
    clientName: [], buName: [], startDate: DEFAULT_DATES.startDate, endDate: DEFAULT_DATES.endDate,
  });
  const [clientList,   setClientList]   = React.useState<string[]>([]);
  const [buList,       setBuList]       = React.useState<{ label: string; value: string }[]>([]);
  const [parentOppData, setParentOppData] = React.useState<any[]>([]);
  const [allChartData,  setAllChartData]  = React.useState<any[]>([]);
  const [loading,       setLoading]       = React.useState(false);
  const [pageNo,        setPageNo]        = React.useState(0);
  const [pageSize,      setPageSize]      = React.useState(10);
  const [totalCount,    setTotalCount]    = React.useState(0);
  const [tableFilters,  setTableFilters]  = React.useState<any[]>([]);
  const [searchText,    setSearchText]    = React.useState("");
  const [searchedColumn,setSearchedColumn]= React.useState("");
  const searchInput = React.useRef<InputRef>(null);

  /* drill-down drawer */
  const [drillOpen,   setDrillOpen]   = React.useState(false);
  const [drillTarget, setDrillTarget] = React.useState<any | null>(null);

  const openDrill = (record: any) => { setDrillTarget(record); setDrillOpen(true); };

  const getSelectedBu = (buArr: { label: string; value: string }[]) => buArr.map((b) => b.value);

  const fetchChartData = (filters: FilterValues) => {
    dispatch(fetchOBOReport_ChartData({
      payload: { clientName: filters.clientName, buName: getSelectedBu(filters.buName), startDate: filters.startDate, endDate: filters.endDate },
    })).then((res: any) => {
      if (res.type === "oboReports/fetchOBOReport_ChartData/fulfilled") {
        setAllChartData(res.payload?.length > 0 ? res.payload : []);
      }
    });
  };

  const fetchOpps = (filters: FilterValues, page: number, size: number, colFilters: any[]) => {
    setLoading(true);
    dispatch(fetchOBOReport_AllOpportunities({
      payload: { clientName: filters.clientName, buName: getSelectedBu(filters.buName), startDate: filters.startDate, endDate: filters.endDate, page, size, filters: colFilters },
    })).then((res: any) => {
      setLoading(false);
      if (res.type === "oboReports/fetchOBOReport_AllOpportunities/fulfilled") {
        if (res.payload.content?.length > 0) {
          setParentOppData(addKeys(res.payload.content));
          setTotalCount(res.payload.totalElements);
          setPageNo(page);
        } else {
          message.warning("No data found for the selected filters.");
          setParentOppData([]);
        }
      } else { rejectedApiMessage(res.payload); }
    });
  };

  useEffect(() => {
    dispatch(fetchBuAndClientDetails_OBO()).then((res: any) => {
      if (res.type === "orderDashboard/fetchBuAndClientDetails_OBO/fulfilled") {
        const buListData: { label: string; value: string }[] = res.payload?.buList || [];
        const clientListData: string[] = res.payload?.clientList || [];
        setBuList(buListData);
        setClientList(clientListData);
        // Auto-load with all clients + last 6 months
        const defaultFilters: FilterValues = {
          clientName: clientListData,
          buName: [],
          startDate: DEFAULT_DATES.startDate,
          endDate: DEFAULT_DATES.endDate,
        };
        setDefaultFiltersForDrawer(defaultFilters);
        setAppliedFilters(defaultFilters);
        dispatch(setOBOFilterData(defaultFilters));
        fetchOpps(defaultFilters, 0, 10, []);
        fetchChartData(defaultFilters);
      } else { rejectedApiMessage(res.payload); }
    });
  }, []); // eslint-disable-line

  const handleApplyFilters = (filters: FilterValues) => {
    setAppliedFilters(filters);
    setPageNo(0); setTableFilters([]);
    dispatch(setOBOFilterData({ clientName: filters.clientName, buName: filters.buName, startDate: filters.startDate, endDate: filters.endDate }));
    fetchOpps(filters, 0, pageSize, []);
    fetchChartData(filters);
  };

  const handleTableChange = (pagination: TablePaginationConfig, antFilters: any) => {
    const current = (pagination.current || 1) - 1;
    const size    = pagination.pageSize || 10;
    const _filters: any[] = [];
    Object.keys(antFilters).forEach((key) => {
      if (antFilters[key]) _filters.push({ field: key, value: antFilters[key][0], operator: key === "opportunityID" ? "EQUALS" : "LIKE" });
    });
    setTableFilters(_filters);
    dispatch(fetchOBOReport_AllOpportunities({
      payload: { clientName: appliedFilters.clientName, buName: getSelectedBu(appliedFilters.buName), startDate: appliedFilters.startDate, endDate: appliedFilters.endDate, page: current, size, filters: _filters },
    })).then((res: any) => {
      if (res.type === "oboReports/fetchOBOReport_AllOpportunities/fulfilled") {
        setParentOppData(addKeys(res.payload.content));
        setTotalCount(res.payload.totalElements);
        setPageNo(current); setPageSize(size);
      }
    });
  };

  const handleRefresh = () => {
    if (appliedFilters.startDate) handleApplyFilters(appliedFilters);
  };

  const handleDownload = () => {
    dispatch(getOboDownloadReport({ payload: { clientName: appliedFilters.clientName, buName: getSelectedBu(appliedFilters.buName), startDate: appliedFilters.startDate, endDate: appliedFilters.endDate } }))
      .then((res: any) => {
        if (res.type !== "oboReports/getOboDownloadFile/fulfilled") rejectedApiMessage(res.payload);
        else notification.success({ message: "Success", description: "The data for the selected period will be mailed to you." });
      });
  };

  /* ── col search ── */
  const handleColSearch = (keys: string[], confirm: (p?: FilterConfirmProps) => void, dataIndex: string) => {
    confirm(); setSearchText(keys[0]); setSearchedColumn(dataIndex);
  };

  const getColSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleColSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" icon={<SearchOutlined />} size="small" style={{ width: 90 }}
            onClick={() => handleColSearch(selectedKeys as string[], confirm, dataIndex)}>Search</Button>
          <Button size="small" style={{ width: 90 }}
            onClick={() => { setSelectedKeys([]); handleColSearch([], confirm, dataIndex); }}>Reset</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
    onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (v) => { if (v) setTimeout(() => searchInput.current?.select(), 100); },
    render: (text) => searchedColumn === dataIndex ? (
      <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
        searchWords={[searchText]} autoEscape textToHighlight={text?.toString() || ""} />
    ) : text,
  });

  /* ── KPI ── */
  const kpi           = allChartData.length > 0 ? allChartData[0] : null;
  const kpiOpps       = kpi?.totalOpportunity?.totalOpportunity || 0;
  const kpiTCV        = kpi?.totalContractValue?.totalContractValue || 0;
  const kpiOBO        = kpi?.oboValue?.totalOboValue || 0;
  const kpiRevTotal   = kpi?.totalRevenue?.totalRevenve || 0;
  const kpiRevPeriod  = kpi?.totalRevenue?.revenue || 0;
  const oboUtilPct    = kpiTCV > 0 ? Math.min(100, (kpiOBO / kpiTCV) * 100) : 0;

  const activeFilterCount = [
    appliedFilters.clientName.length > 0,
    appliedFilters.buName.length > 0,
    !!appliedFilters.startDate,
  ].filter(Boolean).length;

  /* ── parent opp columns ── */
  const columns: TableColumnsType<any> = [
    {
      title: "Client", dataIndex: "client", key: "client", width: 155, fixed: "left",
      render: (v: string) => <CellClient val={v} />,
      ...getColSearchProps("client"),
    },
    {
      title: "Opportunity ID", dataIndex: "opportunityID", key: "opportunityID", width: 160,
      render: (v: string) => <CellID val={v} />,
      ...getColSearchProps("opportunityID"),
    },
    {
      title: "Opportunity Name", dataIndex: "opportunityName", key: "opportunityName", width: 220,
      render: (t: string) => (
        <Tooltip title={t}>
          <span style={{ display: "block", maxWidth: 210, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500, color: "#1e293b", fontSize: 13 }}>{t}</span>
        </Tooltip>
      ),
      ...getColSearchProps("opportunityName"),
    },
    { title: "BU",       dataIndex: "bu",       key: "bu",       width: 120, render: (v: string) => <CellBU val={v} /> },
    { title: "Currency", dataIndex: "currency", key: "currency", width: 90,  render: (v: string) => <CellCurrency val={v} /> },
    { title: "Level",    dataIndex: "level",    key: "level",    width: 155,  render: (v: string) => <CellLevel val={v} /> },
    { title: "Contract Value",   dataIndex: "sumOfTCV",     key: "sumOfTCV",     width: 145, render: (v: any) => <CellMoney val={v} color="#0e7490" bg="#ecfeff" /> },
    { title: "Revenue / Period", dataIndex: "revenue",      key: "revenue",      width: 150, render: (v: any) => <CellMoney val={v} color="#b45309" bg="#fffbeb" /> },
    { title: "Total Revenue",    dataIndex: "totalRevenue", key: "totalRevenue", width: 140, render: (v: any) => <CellMoney val={v} color="#15803d" bg="#f0fdf4" /> },
    { title: "OBO Value",        dataIndex: "obo",          key: "obo",          width: 130, render: (v: any) => <CellMoney val={v} color="#7c3aed" bg="#f5f3ff" /> },
    {
      title: "Hierarchy", key: "action", width: 175, fixed: "right",
      render: (_: any, record: any) => {
        const childCount = record.countOfChildOpp || 0;
        const sowCount   = record.countOfSow || 0;
        const hasChildren = childCount > 0;
        const hasSows     = sowCount > 0;
        const nextLabel   = hasChildren ? "Child Opps" : hasSows ? "SOWs" : "Details";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", gap: 5 }}>
              {childCount > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
                  background: "#ede9fe", color: "#6d28d9", whiteSpace: "nowrap",
                }}>{childCount} child</span>
              )}
              {sowCount > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
                  background: "#d1fae5", color: "#065f46", whiteSpace: "nowrap",
                }}>{sowCount} sow</span>
              )}
              {childCount === 0 && sowCount === 0 && (
                <span style={{ fontSize: 10, color: "#cbd5e1" }}>no hierarchy</span>
              )}
            </div>
            <Button
              type="primary" size="small"
              icon={<EyeOutlined />}
              onClick={() => openDrill(record)}
              style={{
                fontSize: 11, fontWeight: 600,
                background: "#034EA2", borderColor: "#034EA2",
                borderRadius: 5, height: 24,
              }}
            >View {nextLabel} →</Button>
          </div>
        );
      },
    },
  ];

  return (
    <Spin spinning={oboReportData.loading || orderDashboardData.loading}>
      <div className="obo-v2-page">

        {/* ═══ HERO ═══ */}
        <div className="obo-v2-hero">
          <div className="obo-v2-hero-top">
            <div className="obo-v2-title-block">
              <h1>Open Book Order Dashboard</h1>
              <p>Click <strong>View</strong> on any opportunity to explore Child Opps → SOWs → Work Orders &nbsp;·&nbsp; Data refreshes daily</p>
            </div>
            <div className="obo-v2-hero-actions">
              {allChartData.length > 0 && (
                <Tooltip title="Report will be mailed to you">
                  <button className="obo-v2-hero-btn ghost" onClick={handleDownload}><DownloadOutlined /> Export</button>
                </Tooltip>
              )}
              <Tooltip title="Refresh with current filters">
                <button className="obo-v2-hero-btn ghost" onClick={handleRefresh} disabled={!appliedFilters.startDate}><ReloadOutlined /></button>
              </Tooltip>
              <button className="obo-v2-hero-btn filter" onClick={() => setFilterDrawerOpen(true)}>
                <FilterOutlined /> Filters
                {activeFilterCount > 0 && <span className="obo-v2-filter-badge">{activeFilterCount}</span>}
              </button>
            </div>
          </div>

          {allChartData.length > 0 && (
            <div className="obo-v2-kpi-strip">
              <Tooltip title={`${kpiOpps} active opportunities`}>
                <div className="obo-v2-kpi-card">
                  <div className="obo-v2-kpi-icon blue"><TeamOutlined /></div>
                  <div className="obo-v2-kpi-label">Total Opps</div>
                  <div className="obo-v2-kpi-value">{kpiOpps}</div>
                  <div className="obo-v2-kpi-sub">active opportunities</div>
                  <div className="obo-v2-kpi-bar-bg"><div className="obo-v2-kpi-bar-fill blue" style={{ width: "55%" }} /></div>
                </div>
              </Tooltip>
              <Tooltip title={fmtFull(kpiTCV)}>
                <div className="obo-v2-kpi-card">
                  <div className="obo-v2-kpi-icon teal"><DollarOutlined /></div>
                  <div className="obo-v2-kpi-label">Contract Value</div>
                  <div className="obo-v2-kpi-value">{fmtShort(kpiTCV)}</div>
                  <div className="obo-v2-kpi-sub">total TCV</div>
                  <div className="obo-v2-kpi-bar-bg"><div className="obo-v2-kpi-bar-fill teal" style={{ width: "70%" }} /></div>
                </div>
              </Tooltip>
              <Tooltip title={`${oboUtilPct.toFixed(1)}% of TCV · ${fmtFull(kpiOBO)}`}>
                <div className="obo-v2-kpi-card">
                  <div className="obo-v2-kpi-icon pink"><TrophyOutlined /></div>
                  <div className="obo-v2-kpi-label">OBO Value</div>
                  <div className="obo-v2-kpi-value">{fmtShort(kpiOBO)}</div>
                  <div className="obo-v2-kpi-sub">{oboUtilPct.toFixed(1)}% of TCV</div>
                  <div className="obo-v2-kpi-bar-bg"><div className="obo-v2-kpi-bar-fill pink" style={{ width: `${oboUtilPct}%` }} /></div>
                </div>
              </Tooltip>
              <Tooltip title={fmtFull(kpiRevTotal)}>
                <div className="obo-v2-kpi-card">
                  <div className="obo-v2-kpi-icon orange"><FundOutlined /></div>
                  <div className="obo-v2-kpi-label">Total Revenue</div>
                  <div className="obo-v2-kpi-value">{fmtShort(kpiRevTotal)}</div>
                  <div className="obo-v2-kpi-sub">revenue recognised</div>
                  <div className="obo-v2-kpi-bar-bg"><div className="obo-v2-kpi-bar-fill orange" style={{ width: kpiTCV > 0 ? `${Math.min(100, (kpiRevTotal / kpiTCV) * 100)}%` : "0%" }} /></div>
                </div>
              </Tooltip>
              <Tooltip title={fmtFull(kpiRevPeriod)}>
                <div className="obo-v2-kpi-card">
                  <div className="obo-v2-kpi-icon blue"><ArrowRightOutlined /></div>
                  <div className="obo-v2-kpi-label">Rev. for Period</div>
                  <div className="obo-v2-kpi-value">{fmtShort(kpiRevPeriod)}</div>
                  <div className="obo-v2-kpi-sub">selected period</div>
                  <div className="obo-v2-kpi-bar-bg"><div className="obo-v2-kpi-bar-fill blue" style={{ width: kpiRevTotal > 0 ? `${Math.min(100, (kpiRevPeriod / kpiRevTotal) * 100)}%` : "0%" }} /></div>
                </div>
              </Tooltip>
            </div>
          )}
        </div>

        {/* ═══ BODY ═══ */}
        <div className="obo-v2-body">

          {/* active filter chips */}
          {activeFilterCount > 0 && (
            <div className="obo-v2-chip-bar">
              <span className="obo-v2-chip-label">Active Filters</span>
              {appliedFilters.clientName.length > 0 && (
                <span className="obo-v2-chip">
                  <TeamOutlined style={{ fontSize: 10 }} /> {appliedFilters.clientName.length} Client{appliedFilters.clientName.length > 1 ? "s" : ""}
                </span>
              )}
              {appliedFilters.buName.length > 0 && (
                <span className="obo-v2-chip">
                  {appliedFilters.buName.map((b) => b.label).join(", ")}
                </span>
              )}
              {appliedFilters.startDate && (
                <span className="obo-v2-chip">
                  {appliedFilters.startDate} → {appliedFilters.endDate}
                </span>
              )}
            </div>
          )}

          {/* empty state */}
          {allChartData.length === 0 && !oboReportData.loading && (
            <div className="obo-v2-empty">
              <div className="obo-v2-empty-icon">📊</div>
              <h4>No Data Yet</h4>
              <p>Click <strong>Filters</strong> in the top-right, select a client and date range, then apply to load opportunities.</p>
            </div>
          )}

          {/* main opportunities table */}
          {allChartData.length > 0 && (
            <div className="obo-v2-table-section">
              <div className="obo-v2-table-header-bar">
                <h3>
                  <AppstoreOutlined style={{ color: "#034EA2" }} />
                  Parent Opportunities
                  {totalCount > 0 && <span className="obo-v2-record-count">{totalCount} records</span>}
                </h3>
                <div style={{ fontSize: 12, color: "#94a3b8", display: "flex", alignItems: "center", gap: 6 }}>
                  <EyeOutlined /> Click <strong>View</strong> on a row to explore its hierarchy in a side panel
                </div>
              </div>
              <Table
                columns={columns}
                dataSource={parentOppData}
                rowKey={(r) => r.opportunityID || r._key}
                loading={loading}
                scroll={{ x: "max-content", y: 480 }}
                onRow={(record) => ({
                  style: { cursor: "default" },
                  className: "obo-v2-opp-row",
                })}
                pagination={{
                  current: pageNo + 1, pageSize, total: totalCount,
                  showSizeChanger: false,
                  showTotal: (t) => `${t} total opportunities`,
                }}
                onChange={handleTableChange}
              />
            </div>
          )}
        </div>

        {/* ═══ DRILL-DOWN DRAWER ═══ */}
        <DrillDownDrawer
          open={drillOpen}
          onClose={() => setDrillOpen(false)}
          rootOpp={drillTarget}
        />

        {/* ═══ FILTER DRAWER ═══ */}
        <OboFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={handleApplyFilters}
          clientList={clientList}
          buList={buList}
          initialValues={appliedFilters}
          defaultValues={defaultFiltersForDrawer}
        />

      </div>
    </Spin>
  );
};

export default OboDashboardV2;

